import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

import mixpanel from "mixpanel-browser";
import xIcon from "../images/x-icon.svg";

// Custom Input Component
const Input = (props) => {
  const { data } = props;
  return <StyledInput {...data} />;
};

const StyledInput = styled.input`
  padding: ${(props) => props.padding || "10px"};
  border: ${(props) => props.border || "1px solid #ccc"};
  border-radius: ${(props) => props.borderRadius || "4px"};
  outline: none;
  font-size: 16px;
  width: 100%;
`;

const StyledButton = styled.button`
  background: black;
  color: white;
  border: none;
  cursor: pointer;
  font-size: 18px;
  padding: 16px 14px;
  border-radius: 12px;
  margin-top: 5px;
  &:hover {
    background: #333;
  }
`;

const XIcon = styled.img`
  width: 28px;
  height: 28px;
  position: absolute;
  top: 30px;
  right: 30px;
  opacity: 0.5;
  cursor: pointer;
`;

// Custom Checkbox Component
const Checkbox = (props) => {
  const { checked, onChange, style } = props;
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ ...style }}
    />
  );
};

// Custom Row Component
const Row = styled.div`
  display: flex;
  align-items: ${(props) => props.alignItems || "center"};
  justify-content: ${(props) => props.justifyContent || "flex-start"};
  gap: ${(props) => props.gap || "0px"};
  margin: ${(props) => props.margin || "0"};
`;

// Custom Text Component
const Text = (props) => {
  const { data } = props;
  const {
    text,
    fontSize,
    fontWeight,
    color,
    margin,
    fontStyle,
    light,
    onClick,
  } = data;
  return (
    <span
      onClick={onClick}
      style={{
        fontSize,
        fontWeight,
        color,
        margin,
        cursor: onClick ? "pointer" : "default",
        ...(light && { opacity: 0.8 }),
      }}
    >
      {text}
    </span>
  );
};

// Custom FormInputWrapper Component
const FormInputWrapper = ({ label, error, labelStyle, children }) => {
  return (
    <div style={{ marginBottom: "5px" }}>
      <label
        style={{
          fontWeight: "500",
          fontSize: "16px",
          marginBottom: "5px",
          display: "block",
        }}
      >
        {label}
      </label>
      {children}
      {error && (
        <div style={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
          {error}
        </div>
      )}
    </div>
  );
};

// Define the spinning animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled component for the spinner
const SpinnerWrapper = styled.div`
  display: inline-block;
  animation: ${spin} 1s linear infinite;
  width: 30px; // Adjust size as needed
  height: 30px;
`;

// SVG for the spinner
const SpinnerIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 50 50"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="25"
      cy="25"
      r="20"
      fill="none"
      stroke="#ffffff"
      strokeWidth="5"
      strokeDasharray="150"
      strokeDashoffset="40"
    />
  </svg>
);

// Spinner component
const Spinner = () => (
  <SpinnerWrapper>
    <SpinnerIcon />
  </SpinnerWrapper>
);

// SignUpModal Component
const SignUpModal = ({ hide, prompt }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const inputPadding = "10px";

  const params = new URLSearchParams(window.location.search);
  const via = params.get("via");
  const ref = params.get("ref");

  // Validate email with a simple regex
  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  // Validate secure password: at least 8 characters, with uppercase, lowercase, and a number
  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }

    // const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
    // return re.test(password);

    return true;
  };

  // Handler for sign up
  const handleSignUp = async () => {
    let newErrors = {};

    if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!validatePassword(password)) {
      newErrors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, and a number.";
    }
    if (!privacyPolicyAccepted) {
      newErrors.privacyPolicy = "You must accept the privacy policy.";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsFetching(true);

      fetch("https://web-production-f06d.up.railway.app/public/signup/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt, email, password, via, ref }),
      })
        .then((res) => res.json()) // Convert response to JSON
        .then((data) => {
          const access = data.access_token;
          const refresh = data.refresh_token;
          setIsFetching(false);
          mixpanel.identify(email);
          // You can add more logic here to handle the response
          window.location.href = `https://v2.frontly.ai/admin/onboarding?mAccess=${access}&mRefresh=${refresh}`;
        })
        .catch((error) => {
          console.error("Error:", error); // Handle any errors
          setIsFetching(false);
        });
    }
  };

  return (
    <ModalWrapper>
      <Container>
        <FormContainer>
          <XIcon src={xIcon} alt="Close" onClick={hide} />
          <Logo
            src="https://res.cloudinary.com/frontly/image/upload/v1737557733/frontly_black_abjsh6_hn3daa.svg"
            alt="Logo"
          />
          <Headline>Create a free account to continue</Headline>
          <FormInputWrapper
            label={"Email"}
            error={errors.email}
            labelStyle="bodyMdMedium"
          >
            <Input
              data={{
                placeholder: "Email",
                border: errors.email ? "1px solid red" : undefined,
                padding: inputPadding,
                borderRadius: "12px",
                onChange: (e) => setEmail(e.target.value),
              }}
            />
          </FormInputWrapper>
          <FormInputWrapper
            label={"Password"}
            error={errors.password}
            labelStyle="bodyMdMedium"
          >
            <Input
              data={{
                placeholder: "Password",
                type: "password",
                border: errors.password ? "1px solid red" : undefined,
                padding: inputPadding,
                borderRadius: "12px",
                onChange: (e) => setPassword(e.target.value),
              }}
            />
          </FormInputWrapper>

          <Row alignItems="center" gap="10px" margin="5px 0 0 0">
            <Checkbox
              checked={privacyPolicyAccepted}
              onChange={() => setPrivacyPolicyAccepted(!privacyPolicyAccepted)}
              style={{ padding: "0px" }}
            />
            <div style={{ fontSize: "14px" }}>
              I agree to Frontly&apos;s{" "}
              <span
                onClick={() =>
                  window.open(
                    "https://help.frontly.ai/en/articles/8049500-frontly-privacy-policy"
                  )
                }
                style={{
                  color: "var(--primary-admin)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Privacy Policy
              </span>{" "}
              and{" "}
              <span
                onClick={() =>
                  window.open(
                    "https://help.frontly.ai/en/articles/9892485-frontly-terms-of-use"
                  )
                }
                style={{
                  color: "var(--primary-admin)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Terms of Use
              </span>
            </div>
          </Row>
          {errors.privacyPolicy && (
            <Text
              data={{
                text: errors.privacyPolicy,
                fontStyle: "bodySm",
                color: "red",
              }}
            />
          )}

          <StyledButton onClick={handleSignUp}>
            {isFetching ? <Spinner /> : "Create Account"}
          </StyledButton>
        </FormContainer>
      </Container>
    </ModalWrapper>
  );
};

export default SignUpModal;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
  background: rgba(0, 0, 0, 0.5);
  font-family: "Manrope", "Roboto", sans-serif;
  @media (max-width: 1000px) {
    align-items: flex-start;
  }
`;

const Container = styled.div`
  padding: 30px;
  display: flex;
  justify-content: center;
  max-width: 800px;
  gap: 30px;
  border-radius: 30px;
  background: white;
  position: relative;
  @media (max-width: 1000px) {
    flex-direction: column;
    gap: 40px;
    padding: 20px;
  }
`;

const Headline = styled.div`
  font-size: 36px;
  font-weight: 400;
  text-align: center;
  color: var(--text);
  margin-bottom: 10px;
  @media (max-width: 1000px) {
    font-size: 42px;
  }
`;

const FormContainer = styled.div`
  background: white;
  padding: 20px;
  display: flex;
  flex-direction: column;
  border-radius: 25px;
  width: 420px;
  gap: 16px;
  @media screen and (max-width: 800px) {
    width: 100%;
    gap: 15px;
    padding: 25px;
  }
`;

const Logo = styled.img`
  height: 54px;
  width: 100%;
  object-fit: contain;
  @media screen and (max-width: 800px) {
    height: 45px;
  }
`;
